import EnterpriseIcon, { HelpIcon, MenuIcon } from '@enterprise-ui/icons'
import { useAuth } from '@praxis/component-auth'
import {
  useIsFetching,
  useIsMutating,
  useQueryClient,
} from '@tanstack/react-query'
import { useCallback, useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { trackLogOut } from '../../common/fireflyEvents'
import {
  getSelectedAccount,
  getFirstName,
  removeLocalStorageItems,
  setSelectedAccount as setCurrentAccount,
  getLoggedInVendorDetail,
  isPOLDomain,
} from '../../common/helper'
import { isLoggedInAsInternalUser } from '../../common/userAccessHelpers'
import {
  GET_TEMPLATES_KEY,
  CHOOSE_ACCOUNT,
  ACCESS_RIK_INFO,
} from '../../constants'
import useVendorOptions from '../../hooks/useVendorOptions'
import LoggedInVendorDetails from './LoggedInVendorDetails'
import apiConfig from '../../config/apiConfig'
import AccountDropdown from './subcomponents/AccountDropdown'
import Navigation from './subcomponents/Navigation'
import ProfileDropdown from './subcomponents/ProfileDropdown'
import SideNav from './subcomponents/SideNav'
import {
  AppBarContent,
  HeaderContent,
  HeaderWrapper,
  HelpButton,
  LogoContainer,
  MenuButton,
  ToolsContainer,
} from './styles'
import { useSearchParams } from 'react-router-dom'
import {
  parseContextParam,
  NavigationContext,
  clearStoredContext,
  getStoredContext,
  DEFAULT_CONTEXT,
} from './subcomponents/ContextualNavigation'

interface Props {
  userDetails: any
}

const HeaderBlock = (props: Props) => {
  const [searchParams] = useSearchParams()
  const { userDetails } = props
  const queryClient = useQueryClient()
  const auth = useAuth()
  const vendorOptions = useVendorOptions()
  const { user_type } = userDetails || {}
  const [supportURL, setSupportURL] = useState<string>('')
  const { pathname } = useLocation()
  const isLoading = useIsMutating() + useIsFetching()

  // Parse navigation context from URL query param - initialize synchronously to avoid flicker
  const [navigationContext, setNavigationContext] =
    useState<NavigationContext | null>(() => parseContextParam(searchParams))

  useEffect(() => {
    const context = parseContextParam(searchParams)
    if (context !== navigationContext) {
      setNavigationContext(context)
    }
  }, [searchParams, navigationContext])

  const isExternalUser = useCallback(() => {
    return ['EXTERNAL_OTHERS', 'EXTERNAL_RMS', 'EXTERNAL_TARGET_PLUS'].includes(
      user_type,
    )
  }, [user_type])

  const signOut = useCallback(() => {
    trackLogOut()
    removeLocalStorageItems(['selected_vendor', 'external_vendor_detail'])
    clearStoredContext() // Clear the navigation context on logout
    auth.logout()
    queryClient.clear()
  }, [queryClient, auth])

  const currentAccount = getSelectedAccount()
  const userName = getFirstName()

  const defaultAccountSelect =
    !!user_type && !isExternalUser() && !currentAccount
  const [isSwitchAccountVisible, setSwitchAccountVisible] =
    useState<boolean>(defaultAccountSelect)
  const [isSideNavOpen, setSideNavOpen] = useState<boolean>(false)

  const selectedVendor = isExternalUser()
    ? getLoggedInVendorDetail()
    : getSelectedAccount()

  const selectedAccountObject = useVendorOptions(true).filter(
    (vendor: any) => vendor?.id === selectedVendor?.id,
  )

  // URL Helpers
  const getRmsURL = useCallback(() => {
    if (isPOLDomain(window.location.origin)) {
      return apiConfig?.rmsPolURL
    }
    return apiConfig?.rmsEnterpriseURL
  }, [])

  const getPlanningURL = useCallback(() => {
    if (isPOLDomain(window.location.origin)) {
      return apiConfig?.roundelPlanningPolURL
    }
    return apiConfig?.roundelPlanningEnterpriseURL
  }, [])

  const getRmsSupportCenterURL = useCallback(() => {
    if (isPOLDomain(window.location.origin)) {
      return apiConfig?.roundelHelpCenterPolURL
    }
    return apiConfig?.roundelHelpCenterEnterpriseURL
  }, [])

  const getRMSPLandingPage = useCallback(() => {
    if (!selectedVendor?.id) return ''
    const { isTargetPlusAccount = false } = selectedAccountObject[0] || {}
    const baseURL = getRmsURL()
    const pathPrefix = isTargetPlusAccount ? 'v' : 'b'
    return `${baseURL}/?${pathPrefix}=${selectedVendor?.id}`
  }, [selectedVendor?.id, selectedAccountObject, getRmsURL])

  const getRmsBillingURL = useCallback(() => {
    if (!selectedVendor?.id) return ''
    const { isTargetPlusAccount = false } = selectedAccountObject[0] || {}
    const baseURL = getRmsURL()
    const pathPrefix = isTargetPlusAccount ? 'v' : 'b'
    return `${baseURL}/${pathPrefix}/${selectedVendor?.id}/billing`
  }, [selectedVendor?.id, selectedAccountObject, getRmsURL])

  const getRmsCampaignURL = useCallback(() => {
    if (!selectedVendor?.id) return ''
    const { isTargetPlusAccount = false } = selectedAccountObject[0] || {}
    const baseURL = getRmsURL()
    const pathPrefix = isTargetPlusAccount ? 'v' : 'b'
    return `${baseURL}/${pathPrefix}/${selectedVendor?.id}/campaigns`
  }, [selectedVendor?.id, selectedAccountObject, getRmsURL])

  const getPlanningMenuURL = useCallback(() => {
    if (!selectedVendor?.id) return ''
    const baseURL = getPlanningURL()
    return `${baseURL}/planning/b/${selectedVendor?.id}/campaign-briefs`
  }, [selectedVendor?.id, selectedAccountObject, getPlanningURL])

  const getInsightsURL = useCallback(() => {
    // Use the same fallback logic as getVisibleTabs: navigationContext -> stored context -> DEFAULT_CONTEXT
    const effectiveContext =
      navigationContext || getStoredContext() || DEFAULT_CONTEXT
    return `${apiConfig?.rpiURL}?from_app=${effectiveContext}`
  }, [apiConfig, navigationContext])

  const handleAccountChange = useCallback(
    (account: any) => {
      let shouldReload = true
      const vendorDetails = getSelectedAccount()
      if (vendorDetails && vendorDetails?.id !== account?.id) {
        shouldReload = false
      }
      setCurrentAccount(account)
      const pathUrl = window.location.href
      if (pathUrl.includes('/view?t=') || pathUrl.includes('/view/')) {
        queryClient.invalidateQueries({
          queryKey: [GET_TEMPLATES_KEY],
        })
        if (shouldReload) {
          window.location.reload()
        } else {
          window.location.href = '/'
        }
      } else {
        window.location.reload()
      }
    },
    [queryClient],
  )

  // Effects
  useEffect(() => {
    if (apiConfig && Object.keys(apiConfig).length > 1) {
      setSupportURL(
        `${getRmsSupportCenterURL()}${apiConfig?.supportURLs?.RMS?.help}`,
      )
    }
  }, [apiConfig, getRmsSupportCenterURL])

  useEffect(() => {
    if (isExternalUser()) {
      setSwitchAccountVisible(false)
    } else if (!currentAccount && vendorOptions.length > 0) {
      setSwitchAccountVisible(true)
    }
  }, [currentAccount, vendorOptions, isExternalUser])

  // Close the mobile side navigation when the viewport grows past the
  // mobile breakpoint (matches the `max-width: 900px` media query in styles).
  useEffect(() => {
    const mediaQuery = window.matchMedia('(min-width: 901px)')
    const handleViewportChange = (event: MediaQueryListEvent) => {
      if (event.matches) {
        setSideNavOpen(false)
      }
    }
    mediaQuery.addEventListener('change', handleViewportChange)
    return () => mediaQuery.removeEventListener('change', handleViewportChange)
  }, [])

  return (
    <>
      {/* Main Header */}
      <HeaderWrapper>
        {/* Side Navigation - Mobile drawer */}
        <SideNav
          isOpen={isSideNavOpen}
          onClose={() => setSideNavOpen(false)}
          context={navigationContext}
          isExternalUser={isExternalUser()}
          isInternalUser={isLoggedInAsInternalUser()}
          pathname={pathname}
          insightsURL={getInsightsURL()}
          selectedAccount={selectedVendor}
          accounts={vendorOptions}
          onAccountChange={handleAccountChange}
          getRmsCampaignURL={getRmsCampaignURL}
          getRmsBillingURL={getRmsBillingURL}
          getPlanningURL={getPlanningMenuURL}
        />
        <HeaderContent>
          {/* Logo */}
          <LogoContainer to={getRMSPLandingPage()}>
            <img
              src="/RMS_Logo.svg"
              alt="logo"
              aria-label="Link Roundel Reports Home"
              width={'inherit'}
              height={'64px'}
              style={{ objectFit: 'contain' }}
            />
          </LogoContainer>

          {/* Mobile menu toggle */}
          <MenuButton
            aria-label="Open navigation menu"
            onClick={() => setSideNavOpen(true)}
          >
            <EnterpriseIcon icon={MenuIcon} size="lg" />
          </MenuButton>

          {/* App Bar Content - Desktop */}
          <AppBarContent>
            <AccountDropdown
              selectedAccount={selectedVendor}
              accounts={vendorOptions}
              onAccountChange={handleAccountChange}
              isSwitchAccountVisible={isSwitchAccountVisible}
              setSwitchAccountVisible={setSwitchAccountVisible}
              isExternalUser={isExternalUser()}
              isLoading={isLoading}
            >
              <LoggedInVendorDetails
                vendorOptions={vendorOptions}
                setSwitchAccountVisible={setSwitchAccountVisible}
                currentAccount={currentAccount}
              />
            </AccountDropdown>

            <Navigation
              context={navigationContext}
              isInternalUser={isLoggedInAsInternalUser()}
              pathname={pathname}
              insightsURL={getInsightsURL()}
              getRmsCampaignURL={getRmsCampaignURL}
              getRmsBillingURL={getRmsBillingURL}
              getPlanningURL={getPlanningMenuURL}
            />
          </AppBarContent>

          {/* Tools */}
          <ToolsContainer>
            {supportURL && (
              <HelpButton
                aria-label="Link Help"
                to={supportURL}
                target="_blank"
                rel="noopener noreferrer"
                role="link"
              >
                <EnterpriseIcon icon={HelpIcon} size="lg" />
              </HelpButton>
            )}
            <ProfileDropdown userName={userName} onSignOut={signOut} />
          </ToolsContainer>
        </HeaderContent>
      </HeaderWrapper>
    </>
  )
}
export default HeaderBlock
